// @flow
import * as React from 'react';
import { themr, type ThemeType } from 'react-css-themr';
import { compose } from 'recompose';
import { debounce } from 'lodash';
import { type TFunction } from 'react-i18next';

import mapTheme from './fpMapTheme.scss';
import theme from './FpPlanMap.scss';

import GoogleMap from 'common/components/core/map/BaseGoogleMap';
import MapWithFitToBoundFactory from 'common/components/core/map/MapWithFitToBounds';
import mapWithRouteRestrictions from 'common/components/core/map/MapWithRouteRestrictions';
import {
    MarkerType,
    FitToBoundStrategyEnum,
    type FitToBoundStrategyType
} from 'common/enums/MapEnum';
import { FpPlanMapWithSpideringFactory } from './FpPlanMapWithSpidering';
import { MapWithClusteringfactory } from 'common/components/core/map/GoogleMapWithClustering';
import { AnimatedClusterMarker } from 'common/components/core/markers/ClusterMarker';
import {
    memoizedImmutable,
    memoizeNamedImmutable
} from 'common/utils/Memoizer';
import { Polyline } from 'common/components/core/map/Polyline';
import PolygonContainer from 'common/components/core/map/PolygonContainer';
import PlanContextMenu from 'common/entity/pjpMaster/planView/FpPlanContextMenu';
import HandleOutsideClick from 'common/components/core/util/HandleOutsideClick';
import { KeyEventHandler } from 'common/components/core/util/KeyEventsHandler';
import DifferentiableColorPicker from 'common/utils/DifferentiableColorPicker';

import { dashboardColors, neoUIColors } from 'common/enums/ColorEnum';
import { AnimatedSpiderFpPlanVisitMarker } from './FpPlanVisitMarker';
import { PlanStatusEnum } from 'common/enums/PlanEnums';

import { PlanEditsV2 } from 'common/models/internal/PlanEditsV2';

import { type Plan } from 'common/models/internal/Plan';
import { type List, type Map } from 'immutable';

import { type VisitIndexReferenceType } from 'common/types/common.types';

import type { VisitIdOutletType } from 'common/entity/pjpMaster/planView/FpPlanDetailsContainer';

import { withErrorBoundary } from 'common/components/core/errors';
import { AllocatedTasks } from 'common/models/internal/PlanAllocated';
import { PlanUnallocated } from 'common/models/internal/PlanUnallocated';

import { Containers, type ContainerType } from 'common/enums/PlanFilterEnum';
import { type TourVisitIdentifierType } from 'common/contexts/VisitStateContext';

import { FpBeat } from 'common/models/internal/FpBeat';
import FpBeatMarkers, { type Props as TaskMarkerProps } from './FpBeatMarkers';
import FpPlanMapOptions, { FpMapColorByOptions } from './FpPlanMapOptions';
import { PolygonOptionTypes } from 'common/entity/pjpMaster/planView/FpAllocatedVisitsContainer';
import type UnitConversionHelper from 'common/helpers/UnitConversionHelper';
import { type MasterOutlet } from 'common/models/internal/MasterOutlet';

export type VisitReferenceType = {
    tourId: string,
    sourceId: string,
    visitId: string,
    visitSource?: string /* VisitSourceEnum */,
    transactionType?: string
};
type FpMapOptionTypes = {
    showMarkers: boolean,
    aerialRoute: boolean,
    polygon: boolean,
    showUnassignedMarkers: boolean,
    allPolygons: boolean,
    colorByType: string
};
const defaultCenter = { lat: 12.945335, lng: 77.618594 };
const defaultZoom = 13;
type Props = {
    clientId: string,
    pjpId: string,
    plan: Plan,
    options: FpMapOptionTypes,
    theme: ThemeType,
    unallocatedTasks: ?PlanUnallocated,
    assignedTasks: List<AllocatedTasks>,
    hoveredVisit: ?TourVisitIdentifierType,
    activeVisit: ?TourVisitIdentifierType,
    selectedVisits: List<TourVisitIdentifierType>,
    tempHoveredVisit?: VisitReferenceType,
    forceFitBounds: boolean,
    fitToBoundStrategy: ?FitToBoundStrategyType,
    isDataLoading?: boolean,
    showAllPolygonClusters?: boolean,
    planEdits: PlanEditsV2,
    disableFurtherEdits: boolean,
    parentElRefForMarker: {
        current: ?HTMLDivElement
    },
    forceMapFitToBounds: (boolean, ?FitToBoundStrategyType) => void,
    onUserUpdateMap: (?Object) => void,
    selectedRows: List<string>,
    t: TFunction,
    setUpdateAsMapMoves: (updateAsMapMoves: boolean) => void,
    getContainer: () => ContainerType,
    beatSelectionMap: Map<string, boolean>,
    enableContextMenu: boolean,
    visibleBeats: Array<FpBeat>,
    visitIdMap: Map<string, VisitIdOutletType>,
    onContextMenuClose: () => void,
    onContextMenuOpen: (
        x: number,
        y: number,
        beatId: string,
        visitId: string
    ) => void,
    onContextMenuOpenMap: (
        x: number,
        y: number,
        beatId: string,
        visitId: string
    ) => void,
    onVisitClick: (beatId: string, visitId: string) => void,
    onVisitDeselect: () => void,
    onVisitHover: (beatId: string, visitId: string) => void,
    contextMenu: ?Object,
    salesCategoryColorMap: Map<string, string>,
    riderIdColorMap: Map<string, string>,
    weekColorMap: Map<string, { code: string }>,
    weekDayColorMap: Map<string, { code: string }>,
    mapOptions: FpMapOptionTypes,
    currentHoveredVisit: ?string,
    hoveredBeat: ?string,
    selectedVisit: ?string,
    selectedBeat: ?string,
    unallocatedVisits: Array<MasterOutlet>,
    onOptionsChange: (options: Object) => void,
    status: string,
    selectedUnallocatedVisit: ?string,
    hoveredUnallocatedVisit: ?string,
    onUnallocatedVisitContextMenuOpen: (
        x: number,
        y: number,
        visitId: string
    ) => void,
    openMoveVisitDialog: (visitId: string, beatId: string) => void,
    onUnallocatedVisitSelect: (visitId: string) => void,
    onUnallocateVisit: (beat: string, visit: string) => void,
    onUnallocatedVisitHover: (visitId: string) => void,
    onUnallocatedVisitHoverOut: () => void,
    beats: List<FpBeat>,
    visibleBeatColorMap: Map<string, string>,
    filteredBeats: List<FpBeat>,
    UnitConverter: UnitConversionHelper,
    maxEditLimitReached: boolean
};

type State = {
    updateAsMapMoves: boolean,
    hoveredElement?: ?HoveredElementType,
    contextId?: ?string,
    mapOptions: FpMapOptionTypes,
    mapHelper: ?MapHelperType
};

export type MapHelperType = {
    map: ?any,
    maps: ?any
};

type HoveredElementType = {
    tooltip: React.Element<any>,
    latLng: Object,
    meta: any
};

type OnMapLoadParams = { map: any, maps: any };

const MapWithFitToBoundsAndRouteRestrictions = mapWithRouteRestrictions(
    MapWithFitToBoundFactory(GoogleMap)
);

const EnhancedPlanContextMenu = HandleOutsideClick(
    KeyEventHandler(PlanContextMenu)
);

const SpiderMap = MapWithClusteringfactory(
    MapWithFitToBoundsAndRouteRestrictions,
    AnimatedClusterMarker,
    AnimatedSpiderFpPlanVisitMarker
);

const FpPlanMapWithSpidering = FpPlanMapWithSpideringFactory(SpiderMap);

class FpPlanMap extends React.Component<Props, State> {
    map: MapWithFitToBoundsAndRouteRestrictions;
    static defaultProps = {
        enableContextMenu: false
    };

    constructor(props) {
        super(props);
        this.state = {
            mapOptions: props.mapOptions,
            mapHelper: null,
            updateAsMapMoves: false,
            contextId: null,
            hoveredElement: null
        };
    }

    componentDidMount() {
        if (!this.mapOptionsRef) {
            return;
        }
        const { getContainer, mapOptions } = this.props;
        const activeContainer = getContainer();
        const isAllocatedContainer = activeContainer === 'allocated';
        const mapOptionsToRender = {
            showUnassignedMarkers: true,
            polygon: mapOptions.polygon,
            polygonOption: mapOptions.polygonOption,
            aerialRoute: isAllocatedContainer,
            showMarkers: mapOptions.showMarkers
        };
        this.mapOptionsRef
            .getWrappedInstance()
            .updateMapOptions(mapOptionsToRender);
    }

    componentDidUpdate(prevProps: Props) {
        const { beatSelectionMap: prevBeatSelectionMap } = prevProps;
        const { beatSelectionMap, visibleBeats, visitIdMap } = this.props;
        const { forceFitBounds, forceMapFitToBounds } = this.props;
        if (forceFitBounds) {
            this.declareAutoZooming();
            forceMapFitToBounds(false, FitToBoundStrategyEnum.NEXT_RENDER);
        }
        if (
            beatSelectionMap &&
            !beatSelectionMap.equals(prevBeatSelectionMap)
        ) {
            this.declareAutoZooming();
            if (!this.state.updateAsMapMoves) {
                this.map.getWrappedInstance().fitToBound();
            }
        }
        this.lastSetBounds = this.getBounds({
            visitIdMap,
            visibleBeats,
            beatSelectionMap
        });
    }

    componentWillUnmount() {
        this.onVisitHover.cancel();
    }

    HOVER_DELAY_TIME = 200;
    hoverActive = false;

    declareAutoZooming = () => {
        this.autoZooming = true;
        setTimeout(() => {
            this.autoZooming = false;
        }, 500);
    };

    //Map options

    calculateCurrentChildrenBounds = () => {
        const { visitIdMap, beatSelectionMap, visibleBeats } = this.props;
        return this.getBounds({
            visitIdMap,
            visibleBeats,
            beatSelectionMap
        });
    };

    onHover = ({
        tooltip,
        latLng,
        meta
    }: {
        tooltip: React.Element<any>,
        latLng: Object,
        index: number,
        meta: any
    }) => {
        this.setState({ hoveredElement: { tooltip, latLng, meta } });
    };

    onHoverOut = () => {
        this.setState({ hoveredElement: null });
    };

    onContextMenuUnallocatedVisits = (
        x: number,
        y: number,
        visitId: string
    ) => {
        const { onUnallocatedVisitContextMenuOpen } = this.props;
        const contextId = onUnallocatedVisitContextMenuOpen(x, y, visitId);
        this.setState({ contextId });
    };

    onContextMenu = (x: number, y: number, beatId: string, visitId: string) => {
        const { onContextMenuOpen } = this.props;
        if (onContextMenuOpen) {
            const contextId = onContextMenuOpen(x, y, beatId, visitId);
            this.setState({ contextId });
        }
    };
    closeContextMenu = () => {
        const { onContextMenuClose } = this.props;
        this.setState({ contextId: null }, () => {
            onContextMenuClose && onContextMenuClose();
        });
    };
    getContextMenuPortalRef = ref => {
        this.contextMenuPortalRef = ref;
    };

    getContextMenuContainer = () => {
        return this.contextMenuPortalRef;
    };

    getExtraContainerRef = ref => {
        this.extraContainerRef = ref;
    };

    getExtraContainer = () => {
        if (this.extraContainerRef) {
            return this.extraContainerRef;
        }
        return null;
    };

    handleUnaallocatedMoveVisit = () => {
        const {
            openMoveVisitDialog,
            selectedUnallocatedVisit,
            maxEditLimitReached
        } = this.props;
        if (!maxEditLimitReached) {
            openMoveVisitDialog(selectedUnallocatedVisit);
            this.setState({ contextId: null });
        }
    };

    handleMoveVisitClick = () => {
        const {
            openMoveVisitDialog,
            selectedVisit,
            selectedBeat,
            maxEditLimitReached
        } = this.props;
        if (!maxEditLimitReached) {
            openMoveVisitDialog(selectedVisit, selectedBeat);
            this.setState({ contextId: null });
        }
    };

    handleRemoveEvent = () => {
        const {
            onUnallocateVisit,
            selectedBeat,
            selectedVisit,
            maxEditLimitReached
        } = this.props;
        if (!maxEditLimitReached) {
            onUnallocateVisit(selectedBeat, selectedVisit);
            this.setState({ contextId: null });
        }
    };

    getContainerRefOutsideClick = () => {
        const { parentElRefForMarker } = this.props;
        return parentElRefForMarker;
    };

    renderContextMenu() {
        const {
            clientId,
            plan,
            contextMenu,
            pjpId,
            getContainer,
            t,
            maxEditLimitReached
        } = this.props;
        const { contextId } = this.state;
        if (!contextMenu || contextMenu?.id !== contextId) {
            return [];
        }
        const items =
            contextMenu.isUnallocated || getContainer() === 'unallocated'
                ? [
                      {
                          value: t('entityManagement:fpPlanView.move'),
                          onClick: this.handleUnaallocatedMoveVisit
                      }
                  ]
                : [
                      {
                          value: t('entityManagement:fpPlanView.remove'),
                          onClick: this.handleRemoveEvent
                      },
                      {
                          value: t('entityManagement:fpPlanView.move'),
                          onClick: this.handleMoveVisitClick
                      }
                  ];

        return (
            <EnhancedPlanContextMenu
                clientId={clientId}
                planId={pjpId}
                plan={plan}
                removeAutoAssignOption
                onEscape={this.closeContextMenu}
                getContextMenuPortalRef={this.getContextMenuPortalRef}
                getExtraContainerRef={this.getExtraContainerRef}
                x={contextMenu.x}
                y={contextMenu.y}
                closeContextMenu={this.closeContextMenu}
                handleClickOutside={this.closeContextMenu}
                getExtraContainer={this.getExtraContainer}
                items={items}
                disabled={maxEditLimitReached}
                tooltip={
                    maxEditLimitReached &&
                    t(`entityManagement:fpPlanView.editLimitReached`)
                }
            />
        );
    }

    closeSpiderPopup = () => {
        //closing the spider marker popup
        this.spiderMap?.handleHideSpider();
    };

    onChildMouseDown = (key, childProps) => {
        const { tempHoveredVisit, onVisitDeselect } = this.props;

        if (tempHoveredVisit) {
            // hide spider if its open
            this.closeSpiderPopup();
        } else {
            //opening the SpiderMarker onChildMouseDown
            const { children } = childProps || {};
            if (
                children &&
                children.props &&
                children.props.isSpiderMarker &&
                this.spiderMap
            ) {
                this.spiderMap.handleOpenSpider(key);
                this.setHoveredVisit(null);
                onVisitDeselect();
            }
        }
    };

    @memoizedImmutable()
    getAerialRoute(
        visibleBeats: Array<FpBeat>,
        visitIdMap: Map<string, VisitIdOutletType>,
        mapHelper: ?MapHelperType,
        beatSelectionMap: Map<string, boolean>
    ) {
        const polylines = [];
        const { theme } = this.props;
        const { getContainer } = this.props;
        if (!mapHelper) return polylines;
        if (getContainer() === Containers.Allocated) {
            visibleBeats.forEach((beat, index) => {
                const latLngs = [];
                const { visits } = beat;
                const isBeatSelected = beatSelectionMap.get(beat.beatId);
                if (isBeatSelected) {
                    visits.forEach(visit => {
                        const { outletVisitId } = visit;
                        if (outletVisitId) {
                            const outletData = visitIdMap.get(outletVisitId);
                            const visitLatLng = outletData.address.latLng;

                            latLngs.push(visitLatLng);
                        }
                    });
                    const color = neoUIColors.grey500.code;
                    polylines.push(
                        <Polyline
                            onHover={this.onHover}
                            theme={theme}
                            key={`${beat.beatId}${index}`}
                            meta={{}}
                            mapHelper={mapHelper}
                            path={latLngs}
                            color={color}
                            index={index}
                        />
                    );
                }
            });
        }
        return polylines;
    }

    getOuterPolygonTooltip(beat, color) {
        const { theme } = this.props;
        const { beatId } = beat;
        const { visitCount } = beat.summary;
        return (
            <div className={theme.tooltip}>
                <div className={theme.tooltipTitle} style={{ color }}>
                    <span className={theme.title}>{beatId}</span>
                </div>

                <div className={theme.tooltipMeta}>
                    <span className={theme.taskCount}>{visitCount}</span>
                </div>
            </div>
        );
    }

    @memoizedImmutable()
    getAllPolygonClusters(
        beats: Array<FpBeat>,
        mapHelper: ?MapHelperType,
        colorBy: boolean,
        colorByType: string
    ) {
        const {
            theme,
            salesCategoryColorMap,
            visibleBeatColorMap,
            riderIdColorMap,
            weekColorMap,
            weekDayColorMap
        } = this.props;
        const allPolygons = [];
        const colorPicker = new DifferentiableColorPicker();
        if (!mapHelper) {
            return allPolygons;
        }
        beats.forEach((beat, index) => {
            const key = `${beat.beatId}-polygon`;
            let color;
            const polygonVertices = beat.summary.outerPolygon?.points;
            const [salesCategory] = beat.categories;
            const { currentSalesmanId, beatDay } = beat;
            if (colorBy) {
                if (colorByType === FpMapColorByOptions.SalesCategory) {
                    if (salesCategory) {
                        color = salesCategoryColorMap.get(salesCategory).code;
                    } else {
                        color = visibleBeatColorMap.get(beat.beatId).code;
                    }
                } else if (colorByType === FpMapColorByOptions.Riders) {
                    color =
                        riderIdColorMap.get(currentSalesmanId)?.code ||
                        'grey.200';
                } else if (colorByType === FpMapColorByOptions.Week) {
                    color = weekColorMap.get(beatDay.week).code || 'grey.200';
                } else if (colorByType === FpMapColorByOptions.WeekDay) {
                    color =
                        weekDayColorMap.get(beatDay.dayOfWeek).code ||
                        'grey.200';
                }
            } else {
                color = colorPicker.pick().code;
            }
            const tooltip = this.getOuterPolygonTooltip(beat, color);
            const strokeWeight = 2;
            const fillOpacity = 0.45;
            allPolygons.push(
                <PolygonContainer
                    index={index}
                    color={color}
                    theme={theme}
                    key={key}
                    mapHelper={mapHelper}
                    onHover={this.onHover}
                    onHoverOut={this.onHoverOut}
                    zIndex={2}
                    polygonVertices={polygonVertices}
                    meta={{}}
                    strokeWeight={strokeWeight}
                    strokeOpacity={1}
                    fillOpacity={fillOpacity}
                    tooltip={tooltip}
                />
            );
        });

        return allPolygons;
    }

    @memoizedImmutable()
    getPolygonCluster(
        visibleBeats: Array<FpBeat>,
        visitIdMap: Map<string, VisitIdOutletType>,
        mapHelper: ?MapHelperType,
        beatSelectionMap: Map<string, boolean>,
        colorBy: ?boolean,
        colorByType: string,
        salesCategoryColorMap: Map<string, Object>,
        riderIdColorMap: Map<string, Object>,
        filteredBeatsOnly: boolean
    ) {
        const polygons = [];
        if (!mapHelper) {
            return polygons;
        }
        if (!mapHelper) {
            return polygons;
        }
        const {
            visibleBeatColorMap,
            weekColorMap,
            weekDayColorMap
        } = this.props;
        visibleBeats.forEach((beat, index) => {
            const key = `${beat.beatId}-polygon`;
            const { currentSalesmanId, beatDay } = beat;
            const { points: polygonVertices } = beat.summary.outerPolygon;
            const [salesCategory] = beat.categories;
            let color;
            if (colorBy) {
                if (colorByType === FpMapColorByOptions.SalesCategory) {
                    if (salesCategory) {
                        color = salesCategoryColorMap.get(salesCategory).code;
                    } else {
                        color = visibleBeatColorMap.get(beat.beatId).code;
                    }
                } else if (colorByType === FpMapColorByOptions.Week) {
                    color = weekColorMap.get(beatDay.week).code || 'grey.200';
                } else if (colorByType === FpMapColorByOptions.WeekDay) {
                    color =
                        weekDayColorMap.get(beatDay.dayOfWeek).code ||
                        'grey.200';
                }
                if (colorByType === FpMapColorByOptions.Riders) {
                    color =
                        riderIdColorMap.get(currentSalesmanId)?.code ||
                        'grey.200';
                }
            } else {
                color = visibleBeatColorMap.get(beat.beatId).code;
            }
            const tooltip = this.getOuterPolygonTooltip(beat, color);
            const strokeWeight = 2;
            const fillOpacity = 0.45;
            if (beatSelectionMap.get(beat.beatId) || filteredBeatsOnly) {
                polygons.push(
                    <PolygonContainer
                        index={index}
                        color={color}
                        theme={theme}
                        key={key}
                        mapHelper={mapHelper}
                        onHover={this.onHover}
                        onHoverOut={this.onHoverOut}
                        zIndex={2}
                        polygonVertices={polygonVertices}
                        meta={{}}
                        strokeWeight={strokeWeight}
                        strokeOpacity={1}
                        fillOpacity={fillOpacity}
                        tooltip={tooltip}
                    />
                );
            }
        });

        return polygons;
    }

    onMapLoad = ({ map, maps }: OnMapLoadParams) => {
        this.setState({ mapHelper: { map, maps } }, () => {
            map.setOptions({ scaleControl: true });
        });
    };

    renderMapChildren(): Array<any> {
        const {
            salesCategoryColorMap,
            visitIdMap,
            visibleBeats,
            beatSelectionMap,
            filteredBeats,
            mapOptions,
            beats,
            riderIdColorMap
        } = this.props;

        const { hoveredElement, mapHelper } = this.state;
        const {
            aerialRoute,
            polygon,
            colorBy,
            polygonOption,
            colorByType
        } = mapOptions;
        let polygons = [];
        const allPolygons = [];
        let aerialRoutePath = [];
        let tooltip = [];
        let contextMenu = this.renderContextMenu();
        contextMenu = Array.isArray(contextMenu) ? contextMenu : [contextMenu];

        if (aerialRoute) {
            aerialRoutePath = this.getAerialRoute(
                visibleBeats,
                visitIdMap,
                mapHelper,
                beatSelectionMap
            );
        }

        if (polygon && polygonOption === PolygonOptionTypes.ALL) {
            polygons = this.getAllPolygonClusters(
                beats,
                mapHelper,
                colorBy,
                colorByType
            );
        }
        if (polygon && polygonOption === PolygonOptionTypes.SELECTED) {
            const filteredBeatsOnly = false;
            polygons = this.getPolygonCluster(
                visibleBeats,
                visitIdMap,
                mapHelper,
                beatSelectionMap,
                colorBy,
                colorByType,
                salesCategoryColorMap,
                riderIdColorMap,
                filteredBeatsOnly
            );
        }
        if (polygon && polygonOption === PolygonOptionTypes.FILTERED) {
            const filteredBeatsOnly = true;
            polygons = this.getPolygonCluster(
                filteredBeats,
                visitIdMap,
                mapHelper,
                beatSelectionMap,
                colorBy,
                colorByType,
                salesCategoryColorMap,
                riderIdColorMap,
                filteredBeatsOnly
            );
        }

        if (hoveredElement) {
            tooltip = [this.renderTooltip(hoveredElement)];
        }

        return [
            ...polygons,
            ...aerialRoutePath,
            ...tooltip,
            ...contextMenu,
            ...allPolygons
        ];
    }

    getTourMarkers = memoizeNamedImmutable(markerProps => {
        return FpBeatMarkers.getMarkers(markerProps);
    });

    getFlagMarkers = markerProps => {
        return FpBeatMarkers.getFlagMarkers(markerProps);
    };

    getHomebaseMarkers = markerProps => {
        return FpBeatMarkers.getHomebaseMarkers(markerProps);
    };
    getUnallocatedTaskMarkers = memoizeNamedImmutable(
        (markerProps: TaskMarkerProps) => {
            return FpBeatMarkers.getUnallocatedMarkers(markerProps);
        }
    );

    shouldRenderMarkers() {
        const {
            selectedVisits,
            currentHoveredVisit,
            activeVisit,
            options
        } = this.props;
        const { showMarkers, showUnassignedMarkers } = options;
        const hasSelectedVisits = selectedVisits && !selectedVisits.isEmpty();
        return (
            hasSelectedVisits ||
            activeVisit ||
            currentHoveredVisit ||
            showMarkers ||
            showUnassignedMarkers
        );
    }

    setHoveredUnallocatedVisit = (visitId: string) => {
        const { onUnallocatedVisitHover } = this.props;
        onUnallocatedVisitHover(visitId);
    };

    onUnallocatedVisitHover = debounce((visitId: string) => {
        this.hoverActive = true;
        this.setHoveredUnallocatedVisit(visitId);
    }, this.HOVER_DELAY_TIME);

    onUnallocatedVisitHoverOut = () => {
        this.onVisitHover.cancel();
        if (this.hoverActive) {
            this.hoverActive = false;
        }
        this.props.onUnallocatedVisitHoverOut();
    };

    closeUnallocatedVisitPopup = () => {
        const { onUnallocatedVisitDeselect } = this.props;
        this.onUnallocatedVisitHoverOut();
        onUnallocatedVisitDeselect();
    };

    setHoveredVisit = (beatId, visitId) => {
        const { onVisitHover, getContainer } = this.props;
        if (getContainer() === Containers.Allocated) {
            onVisitHover(beatId, visitId);
        }
    };

    onVisitHover = debounce((beatId, visitId) => {
        this.hoverActive = true;
        this.setHoveredVisit(beatId, visitId);
    }, this.HOVER_DELAY_TIME);

    onVisitHoverOut = () => {
        this.onVisitHover.cancel();
        if (this.hoverActive) {
            this.hoverActive = false;
            this.setHoveredVisit(null);
        }
        this.props.onVisitHoverOutside();
    };

    closeVisitPopup = () => {
        const { onVisitDeselect } = this.props;
        this.onVisitHoverOut();
        onVisitDeselect();
    };

    renderMapMarkers(): React.Node {
        const {
            currentHoveredVisit,
            contextMenu,
            parentElRefForMarker,
            getContainer,
            visitIdMap,
            visibleBeats,
            beatSelectionMap,
            hoveredBeat,
            mapOptions,
            selectedVisit,
            selectedBeat,
            onVisitClick,
            pjpId,
            status,
            unallocatedVisits,
            selectedUnallocatedVisit,
            hoveredUnallocatedVisit,
            onUnallocatedVisitSelect,
            UnitConverter,
            t,
            maxEditLimitReached
        } = this.props;

        const { showMarkers, markerType, showUnassignedMarkers } = mapOptions;

        const markerProps = {
            status,
            isDisabled: status === PlanStatusEnum.Frozen,
            onContextMenuOpen: this.onContextMenu,
            markerType,
            contextMenu,
            parentElRefForMarker,
            getContainer,
            pjpId,
            UnitConverter,
            t,
            maxEditLimitReached
        };

        let allocatedVisitMarkers = [];
        let flagMarkers = [];
        let homebaseMarkers = [];
        if (getContainer() === Containers.Allocated && showMarkers) {
            const allocatedMarkerProps = {
                ...markerProps,
                hoveredVisit: currentHoveredVisit,
                hoveredBeat,
                selectedVisit,
                setHoveredVisit: this.setHoveredVisit,
                onHover: this.onVisitHover,
                onHoverOut: this.onVisitHoverOut,
                onPopUpCloseClick: this.closeVisitPopup,
                onVisitClick,
                selectedBeat,
                isDisabled: status === PlanStatusEnum.Frozen,
                visitIdMap,
                visibleBeats,
                beatSelectionMap
            };
            allocatedVisitMarkers = this.getTourMarkers(allocatedMarkerProps);
            flagMarkers = this.getFlagMarkers(allocatedMarkerProps);
            homebaseMarkers = this.getHomebaseMarkers(allocatedMarkerProps);
        }
        let unassignedMarkers = [];
        if (showUnassignedMarkers && showMarkers) {
            const visitMarkerProps = {
                ...markerProps,
                selectedUnallocatedVisit,
                hoveredUnallocatedVisit,
                onContextMenuUnallocatedVisits: this
                    .onContextMenuUnallocatedVisits,
                setHoveredUnallocatedVisit: this.setHoveredUnallocatedVisit,
                onUnallocatedVisitHoverOut: this.onUnallocatedVisitHoverOut,
                onUnallocatedVisitHover: this.onUnallocatedVisitHover,
                onUnallocatedVisitPopUpCloseClick: this
                    .closeUnallocatedVisitPopup,
                onUnallocatedVisitClick: onUnallocatedVisitSelect,
                unallocatedVisits
            };
            unassignedMarkers = this.getUnallocatedTaskMarkers(
                visitMarkerProps
            );
        }

        const markers = [
            ...allocatedVisitMarkers,
            ...unassignedMarkers,
            ...flagMarkers,
            ...homebaseMarkers
        ];

        return markers;
    }
    noOp = () => {};

    renderTooltip(hoveredElement: HoveredElementType) {
        const { theme } = this.props;
        const { tooltip, latLng } = hoveredElement;
        return (
            <span
                key={1}
                lat={latLng.lat()}
                lng={latLng.lng()}
                markerType={MarkerType.NON_HOVERABLE}
                onMouseOver={this.noOp}
                onMouseOut={this.noOp}
                className={theme.mapTooltip}
            >
                {tooltip}
            </span>
        );
    }

    getTourVisitFromTaskList(
        taskList: List<AllocatedTasks> | List<UnassignedTask>,
        visitIndex: VisitIndexReferenceType
    ) {
        const selectedTask = taskList.find(task => {
            const visit = task.visits.get(1);
            return (
                visit.sourceId === visitIndex.sourceId &&
                visit.id === visitIndex.id &&
                visit.transactionType === visitIndex.transactionType
            );
        });
        return selectedTask?.visits.get(1);
    }

    getUnassignedTaskVisitFromIds(taskId, visitId) {
        const { unallocatedTasks } = this.props;
        const task = unallocatedTasks?.tasks.find(
            unassignedTask => unassignedTask.taskId === taskId
        );
        return task && task.visits.find(visit => visit.id === visitId);
    }

    getUnassignedVisitsFromTaskIds(taskId) {
        const { unallocatedTasks } = this.props;
        const task = unallocatedTasks?.tasks.find(
            unassignedTask => unassignedTask.taskId === taskId
        );
        return task && task.visits;
    }

    getAllocatedContainerBounds = (
        beats,
        bounds,
        beatSelectionMap,
        visitIdMap
    ) => {
        const updatedBounds = bounds;
        beats.forEach(beat => {
            const isBeatSelected = beatSelectionMap.get(beat.beatId);
            if (isBeatSelected) {
                const { visits } = beat;
                visits.forEach(visit => {
                    if (visit.outletVisitId) {
                        const { outletVisitId } = visit;
                        const outletData = visitIdMap.get(outletVisitId);
                        const visitLatLng = outletData.address.latLng;
                        updatedBounds.extend(visitLatLng);
                    }
                });
            }
        });
        return updatedBounds;
    };

    getBounds = ({ visitIdMap, visibleBeats, beatSelectionMap }) => {
        const { getContainer, unallocatedVisits } = this.props;
        let bounds = new google.maps.LatLngBounds();
        if (getContainer() === Containers.Allocated) {
            bounds = this.getAllocatedContainerBounds(
                visibleBeats,
                bounds,
                beatSelectionMap,
                visitIdMap
            );
        } else {
            unallocatedVisits.forEach(visit => {
                const visitLatLng = visit.address.latLng;
                bounds.extend(visitLatLng);
            });
        }
        return bounds;
    };

    getSelectedVisitsBounds = () => {
        const {
            visibleBeats,
            visitIdMap,
            beatSelectionMap,
            unallocatedVisits,
            getContainer
        } = this.props;
        let bounds = new google.maps.LatLngBounds();
        if (getContainer() === Containers.Allocated) {
            bounds = this.getAllocatedContainerBounds(
                visibleBeats,
                bounds,
                beatSelectionMap,
                visitIdMap
            );
        } else {
            unallocatedVisits.forEach(visit => {
                const visitLatLng = visit.address.latLng;
                bounds.extend(visitLatLng);
            });
        }
        return bounds;
    };

    createGMapsBounds = bounds => {
        const newBounds = new google.maps.LatLngBounds();
        newBounds.extend(bounds.sw);
        newBounds.extend(bounds.ne);
        return newBounds;
    };

    onDragEnd = () => {
        this.onUserUpdateMap();
    };

    onZoomEnd = () => {
        this.onUserUpdateMap();
        if (
            this.lastSetBounds.equals(this.calculateCurrentChildrenBounds()) &&
            !this.autoZooming
        ) {
            if (this.autoZooming === undefined) {
                //HACK: considering that autoZooming wont be set at the initial load.
                //We really need to fix map interactions 🤦‍♂️
                //TODO: The above does not work, we should fix it.
                return;
            }
            this.onUserUpdateMap();
            return;
        }
    };

    onUserUpdateMap = () => {
        if (!this.map) {
            return;
        }
        if (!this.state.updateAsMapMoves) {
            return;
        }
        const bounds = this.map
            .getWrappedInstance()
            ?.getMapInstance()
            ?.getBounds();

        if (bounds) {
            this.props.onUserUpdateMap?.(bounds);
        }
    };

    mapCallback = (ref: MapWithFitToBoundsAndRouteRestrictions) => {
        this.map = ref;
    };

    getSpiderMapRef = (ref: SpiderMap) => {
        this.spiderMap = ref;
    };

    onUpdateAsMapMovesToggle = (updateAsMapMoves: boolean) => {
        const { setUpdateAsMapMoves } = this.props;
        setUpdateAsMapMoves(updateAsMapMoves);
        this.setState({ updateAsMapMoves }, () => {
            if (!this.state.updateAsMapMoves) {
                // Clear the bounding filter on disabling updateAsMapMoves
                this.props.onUserUpdateMap?.(null);
                return;
            }
            this.onUserUpdateMap();
        });
    };
    overrideClusterConfidenceColor = () => {
        return dashboardColors.slateGrey;
    };

    getMapOptionsRef = ref => {
        this.mapOptionsRef = ref;
    };

    getSelectedMapLegends = () => {
        const {
            mapOptions,
            salesCategoryColorMap,
            riderIdColorMap,
            visibleBeatColorMap,
            weekColorMap,
            weekDayColorMap
        } = this.props;
        const { colorBy, colorByType } = mapOptions;
        if (colorBy) {
            switch (colorByType) {
                case FpMapColorByOptions.SalesCategory:
                    return salesCategoryColorMap;
                case FpMapColorByOptions.Riders:
                    return riderIdColorMap;
                case FpMapColorByOptions.Week:
                    return weekColorMap;
                case FpMapColorByOptions.WeekDay:
                    return weekDayColorMap;
                default:
                    return visibleBeatColorMap;
            }
        }
        return visibleBeatColorMap;
    };

    render() {
        const {
            theme,
            onOptionsChange,
            mapOptions,
            onVisitClick,
            visitIdMap,
            visibleBeats,
            beatSelectionMap,
            getContainer,
            unallocatedVisits,
            filteredBeats,
            UnitConverter,
            t
        } = this.props;
        const { mapHelper } = this.state;
        const bounds = this.getBounds({
            visitIdMap,
            visibleBeats,
            beatSelectionMap
        });
        const { map } = mapHelper || {};

        return (
            <div className={theme.mapWrap}>
                <FpPlanMapOptions
                    {...mapOptions}
                    t={t}
                    ref={this.getMapOptionsRef}
                    theme={theme}
                    getContainer={getContainer}
                    onChange={onOptionsChange}
                    unallocatedVisitCount={unallocatedVisits.length}
                    legendMap={this.getSelectedMapLegends()}
                    filteredBeats={filteredBeats}
                    visibleBeats={visibleBeats}
                    beatSelectionMap={beatSelectionMap}
                    UnitConverter={UnitConverter}
                />
                <FpPlanMapWithSpidering
                    innerRef={this.getSpiderMapRef}
                    defaultMarkerIcon="location-icon"
                    doNotApplyBlackTheme
                    map={map}
                    onClick={onVisitClick}
                    disableInactivityTimeout
                    theme={theme}
                    getRouteRestrictionsComponentMapRef={this.mapCallback}
                    bounds={bounds}
                    selectedElements={this.getSelectedVisitsBounds()}
                    defaultCenter={defaultCenter}
                    defaultZoom={defaultZoom}
                    enableFitToBounds
                    onChildMouseDown={this.onChildMouseDown}
                    onGoogleApiLoaded={this.onMapLoad}
                    mapChildren={this.renderMapChildren()}
                    onDragEnd={this.onDragEnd}
                    onZoomAnimationEnd={this.onZoomEnd}
                    isV2UI
                    overrideClusterConfidenceColor={
                        this.overrideClusterConfidenceColor
                    }
                >
                    {this.renderMapMarkers()}
                </FpPlanMapWithSpidering>
            </div>
        );
    }
}

const ComposedComponent = compose(
    withErrorBoundary({
        minimal: true,
        defaultProps: {
            errorBoundaryTitleKey: 'genericError',
            errorBoundarySubtitleKey: 'errorMessages.adblockerError'
        }
    }),
    themr(null, theme),
    themr(null, mapTheme, { withRef: true })
)(FpPlanMap);

export default ComposedComponent;
